import type {
  AtomicRpcMasterEffect,
  PreparedAtomicRpcMutation,
} from './sync/atomicRpcOutbox.js';

export const PRODUCTION_DAY_TRANSITION_SCHEMA =
  'trackeat.production-day-transition' as const;
export const PRODUCTION_DAY_TRANSITION_RESULT_SCHEMA =
  'trackeat.production-day-transition-result' as const;

export type ProductionDayTransitionKindV1 = 'close' | 'verify' | 'reopen';

export function productionDayConflictRefreshSignalV1(input: {
  status: string;
  lastError: string | null;
  lastPullAt: string | null;
  lastPushAt: string | null;
  pendingCount: number;
}): string {
  // Un pull fallito non cambia lastPullAt/lastPushAt, ma può aver trasformato
  // l'outbox in conflitto. Status ed errore fanno quindi parte del segnale.
  return JSON.stringify([
    input.status,
    input.lastError,
    input.lastPullAt,
    input.lastPushAt,
    input.pendingCount,
  ]);
}

export function isProductionDayEditableV1(input: {
  closedAt: string | null | undefined;
  verifiedAt: string | null | undefined;
}): boolean {
  // Anche un supervisore deve riaprire esplicitamente la scheda: una giornata
  // chiusa è uno snapshot firmato, non una bozza con privilegi elevati.
  return !input.closedAt && !input.verifiedAt;
}

export interface ProductionDaySignedSnapshotV1 {
  sample_meal_taken: boolean;
  sample_meal_at: string | null;
  sample_meal_notes: string | null;
  meals_total: number | null;
  meals_notes: string | null;
  notes: string | null;
  reopen_count: number;
}

export interface ProductionDayTransitionRequestV1 {
  schema: typeof PRODUCTION_DAY_TRANSITION_SCHEMA;
  version: 1;
  request_id: string;
  transition: ProductionDayTransitionKindV1;
  tenant_id: string;
  business_id: string;
  production_day_id: string;
  sheet_date: string;
  expected_version: number;
  device_id: string;
  requested_at: string;
  snapshot: ProductionDaySignedSnapshotV1;
  [key: string]: unknown;
}

type ProductionDayTransitionRequestRecordV1 =
  ProductionDayTransitionRequestV1 & Record<string, unknown>;

export interface ProductionDayTransitionRowV1
  extends ProductionDaySignedSnapshotV1 {
  id: string;
  tenant_id: string;
  business_id: string;
  sheet_date: string;
  closed_at: string | null;
  closed_by: string | null;
  verified_at: string | null;
  verified_by: string | null;
  reopened_at: string | null;
  version: number;
  created_at?: string;
  archived_at: string | null;
  [key: string]: unknown;
}

export interface BuildProductionDayTransitionInputV1 {
  transition: ProductionDayTransitionKindV1;
  requestId: string;
  tenantId: string;
  businessId: string;
  productionDayId: string;
  sheetDate: string;
  expectedVersion: number;
  deviceId: string;
  requestedAt: string;
  snapshot: ProductionDaySignedSnapshotV1;
}

export interface PrepareProductionDayTransitionInputV1
  extends BuildProductionDayTransitionInputV1 {
  actorUserId: string;
  currentRow: ProductionDayTransitionRowV1 | null;
}

function normalizedText(value: string | null): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed || null;
}

function normalizedIso(value: string | null): string | null {
  if (value === null) return null;
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) {
    throw new Error('production_day_transition_invalid_sample_time');
  }
  return parsed.toISOString();
}

function isUuid(value: unknown): value is string {
  return (
    typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      .test(value)
  );
}

function requireUuid(value: string, field: string): void {
  if (!isUuid(value)) {
    throw new Error(`production_day_transition_invalid_${field}`);
  }
}

function requireDate(value: string): void {
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(value)
    || Number.isNaN(Date.parse(`${value}T00:00:00.000Z`))
  ) {
    throw new Error('production_day_transition_invalid_sheet_date');
  }
}

export function normalizeProductionDaySignedSnapshotV1(
  value: ProductionDaySignedSnapshotV1,
): ProductionDaySignedSnapshotV1 {
  if (
    !value
    || typeof value !== 'object'
    || typeof value.sample_meal_taken !== 'boolean'
    || (
      value.sample_meal_at !== null
      && typeof value.sample_meal_at !== 'string'
    )
    || (
      value.sample_meal_notes !== null
      && typeof value.sample_meal_notes !== 'string'
    )
    || (
      value.meals_notes !== null
      && typeof value.meals_notes !== 'string'
    )
    || (
      value.notes !== null
      && typeof value.notes !== 'string'
    )
    || (
      value.meals_total !== null
      && (
        !Number.isSafeInteger(value.meals_total)
        || value.meals_total < 0
      )
    )
    || !Number.isSafeInteger(value.reopen_count)
    || value.reopen_count < 0
  ) {
    throw new Error('production_day_transition_invalid_snapshot');
  }
  return {
    sample_meal_taken: value.sample_meal_taken,
    sample_meal_at: normalizedIso(value.sample_meal_at),
    sample_meal_notes: normalizedText(value.sample_meal_notes),
    meals_total: value.meals_total,
    meals_notes: normalizedText(value.meals_notes),
    notes: normalizedText(value.notes),
    reopen_count: value.reopen_count,
  };
}

export function productionDaySignedSnapshotFromRowV1(
  row: Pick<
    ProductionDayTransitionRowV1,
    | 'sample_meal_taken'
    | 'sample_meal_at'
    | 'sample_meal_notes'
    | 'meals_total'
    | 'meals_notes'
    | 'notes'
    | 'reopen_count'
  >,
): ProductionDaySignedSnapshotV1 {
  return normalizeProductionDaySignedSnapshotV1({
    sample_meal_taken: row.sample_meal_taken,
    sample_meal_at: row.sample_meal_at,
    sample_meal_notes: row.sample_meal_notes,
    meals_total: row.meals_total,
    meals_notes: row.meals_notes,
    notes: row.notes,
    reopen_count: row.reopen_count ?? 0,
  });
}

export function buildProductionDayTransitionRequestV1(
  input: BuildProductionDayTransitionInputV1,
): ProductionDayTransitionRequestV1 {
  requireUuid(input.requestId, 'request_id');
  requireUuid(input.tenantId, 'tenant_id');
  requireUuid(input.businessId, 'business_id');
  requireUuid(input.productionDayId, 'production_day_id');
  requireDate(input.sheetDate);
  if (
    !Number.isSafeInteger(input.expectedVersion)
    || input.expectedVersion < 0
  ) {
    throw new Error('production_day_transition_invalid_expected_version');
  }
  if (!input.deviceId.trim() || input.deviceId.trim().length > 200) {
    throw new Error('production_day_transition_invalid_device_id');
  }
  const requestedAt = new Date(input.requestedAt);
  if (!Number.isFinite(requestedAt.getTime())) {
    throw new Error('production_day_transition_invalid_requested_at');
  }
  return {
    schema: PRODUCTION_DAY_TRANSITION_SCHEMA,
    version: 1,
    request_id: input.requestId,
    transition: input.transition,
    tenant_id: input.tenantId,
    business_id: input.businessId,
    production_day_id: input.productionDayId,
    sheet_date: input.sheetDate,
    expected_version: input.expectedVersion,
    device_id: input.deviceId.trim(),
    requested_at: requestedAt.toISOString(),
    snapshot: normalizeProductionDaySignedSnapshotV1(input.snapshot),
  };
}

export function prepareProductionDayTransitionV1(
  input: PrepareProductionDayTransitionInputV1,
): PreparedAtomicRpcMutation<ProductionDayTransitionRequestRecordV1> {
  requireUuid(input.actorUserId, 'actor_user_id');
  const request = buildProductionDayTransitionRequestV1(input);
  const current = input.currentRow;
  if (current) {
    if (
      current.id !== request.production_day_id
      || current.tenant_id !== request.tenant_id
      || current.business_id !== request.business_id
      || current.sheet_date !== request.sheet_date
      || current.version !== request.expected_version
    ) {
      throw new Error('production_day_transition_local_identity_conflict');
    }
    if (
      request.transition !== 'close'
      &&
      JSON.stringify(productionDaySignedSnapshotFromRowV1(current))
      !== JSON.stringify(request.snapshot)
    ) {
      throw new Error('production_day_transition_local_snapshot_conflict');
    }
  } else if (request.transition !== 'close' || request.expected_version !== 0) {
    throw new Error('production_day_transition_local_day_missing');
  }

  let patch: Record<string, unknown>;
  let op: AtomicRpcMasterEffect['op'] = 'update';
  if (request.transition === 'close') {
    patch = {
      closed_at: request.requested_at,
      closed_by: input.actorUserId,
      ...request.snapshot,
    };
    if (!current) {
      op = 'insert';
      patch = {
        id: request.production_day_id,
        tenant_id: request.tenant_id,
        business_id: request.business_id,
        sheet_date: request.sheet_date,
        ...request.snapshot,
        closed_at: request.requested_at,
        closed_by: input.actorUserId,
        verified_at: null,
        verified_by: null,
        reopened_at: null,
        archived_at: null,
      };
    }
  } else if (request.transition === 'verify') {
    if (
      !current?.closed_at
      || current.verified_at
      || current.closed_by === input.actorUserId
    ) {
      throw new Error('production_day_transition_local_verify_forbidden');
    }
    patch = {
      verified_at: request.requested_at,
      verified_by: input.actorUserId,
    };
  } else {
    if (!current?.closed_at || current.verified_at) {
      throw new Error('production_day_transition_local_reopen_forbidden');
    }
    patch = {
      closed_at: null,
      closed_by: null,
      reopened_at: request.requested_at,
      reopen_count: request.snapshot.reopen_count + 1,
    };
  }

  return {
    request: request as ProductionDayTransitionRequestRecordV1,
    optimisticMasters: [{
      table: 'production_days',
      id: request.production_day_id,
      op,
      base_version: request.expected_version,
      patch,
    }],
  };
}

export interface ProductionDayTransitionResultV1 {
  schema: typeof PRODUCTION_DAY_TRANSITION_RESULT_SCHEMA;
  version: 1;
  request_id: string;
  request_digest: string;
  tenant_id: string;
  business_id: string;
  production_day_id: string;
  sheet_date: string;
  transition: ProductionDayTransitionKindV1;
  production_day: ProductionDayTransitionRowV1;
  event: Record<string, unknown>;
  replayed: boolean;
}

export function parseProductionDayTransitionResultV1(
  value: unknown,
  expected: Pick<
    ProductionDayTransitionRequestV1,
    | 'request_id'
    | 'tenant_id'
    | 'business_id'
    | 'production_day_id'
    | 'sheet_date'
    | 'transition'
    | 'expected_version'
    | 'device_id'
    | 'requested_at'
    | 'snapshot'
  >,
  expectedDigest: string,
): ProductionDayTransitionResultV1 {
  const isRecord = (candidate: unknown): candidate is Record<string, unknown> =>
    !!candidate && typeof candidate === 'object' && !Array.isArray(candidate);
  const isTimestamp = (candidate: unknown): candidate is string =>
    typeof candidate === 'string'
    && Number.isFinite(new Date(candidate).getTime());
  const sameInstant = (left: unknown, right: unknown): boolean =>
    isTimestamp(left)
    && isTimestamp(right)
    && new Date(left).getTime() === new Date(right).getTime();
  const isHash = (candidate: unknown): candidate is string =>
    typeof candidate === 'string' && /^[0-9a-f]{64}$/.test(candidate);
  const mismatch = (): never => {
    throw new Error('production_day_transition_response_identity_mismatch');
  };

  if (!isRecord(value)) {
    throw new Error('production_day_transition_response_invalid');
  }
  const result = value;
  if (
    result.schema !== PRODUCTION_DAY_TRANSITION_RESULT_SCHEMA
    || result.version !== 1
    || result.request_id !== expected.request_id
    || result.tenant_id !== expected.tenant_id
    || result.business_id !== expected.business_id
    || result.production_day_id !== expected.production_day_id
    || result.sheet_date !== expected.sheet_date
    || result.transition !== expected.transition
    || result.request_digest !== expectedDigest
    || typeof result.replayed !== 'boolean'
    || !isHash(expectedDigest)
    || !isRecord(result.production_day)
    || !isRecord(result.event)
  ) {
    mismatch();
  }

  const row = result.production_day as Record<string, unknown>;
  const event = result.event as Record<string, unknown>;
  let rowSnapshot: ProductionDaySignedSnapshotV1;
  let expectedSnapshot: ProductionDaySignedSnapshotV1;
  try {
    rowSnapshot = normalizeProductionDaySignedSnapshotV1({
      sample_meal_taken: row.sample_meal_taken as boolean,
      sample_meal_at: row.sample_meal_at as string | null,
      sample_meal_notes: row.sample_meal_notes as string | null,
      meals_total: row.meals_total as number | null,
      meals_notes: row.meals_notes as string | null,
      notes: row.notes as string | null,
      reopen_count: row.reopen_count as number,
    });
    const requestedSnapshot = normalizeProductionDaySignedSnapshotV1(
      expected.snapshot,
    );
    expectedSnapshot = expected.transition === 'reopen'
      ? {
          ...requestedSnapshot,
          reopen_count: requestedSnapshot.reopen_count + 1,
        }
      : requestedSnapshot;
  } catch {
    return mismatch();
  }

  const expectedVersion = expected.expected_version + 1;
  const versionMatches = (
    Number.isSafeInteger(row.version)
    && Number(row.version) >= 0
    && (
      Number(row.version) === expectedVersion
      // Il primo close può creare la riga: INSERT parte da version=0.
      || (
        expected.transition === 'close'
        && expected.expected_version === 0
        && Number(row.version) === 0
      )
    )
  );
  if (
    row.id !== expected.production_day_id
    || row.tenant_id !== expected.tenant_id
    || row.business_id !== expected.business_id
    || row.sheet_date !== expected.sheet_date
    || row.archived_at !== null
    || !versionMatches
    || JSON.stringify(rowSnapshot) !== JSON.stringify(expectedSnapshot)
    || !isTimestamp(row.created_at)
  ) {
    mismatch();
  }

  const validReopenMetadata = rowSnapshot.reopen_count === 0
    ? row.reopened_at === null
    : isTimestamp(row.reopened_at);
  let expectedEventKind: string;
  let transitionActor: string;
  if (expected.transition === 'close') {
    if (
      !isTimestamp(row.closed_at)
      || !isUuid(row.closed_by)
      || row.verified_at !== null
      || row.verified_by !== null
      || !validReopenMetadata
    ) {
      mismatch();
    }
    expectedEventKind = 'production_day_closed';
    transitionActor = String(row.closed_by);
  } else if (expected.transition === 'verify') {
    if (
      !isTimestamp(row.closed_at)
      || !isUuid(row.closed_by)
      || !isTimestamp(row.verified_at)
      || !isUuid(row.verified_by)
      || row.closed_by === row.verified_by
      || !validReopenMetadata
    ) {
      mismatch();
    }
    expectedEventKind = 'production_day_verified';
    transitionActor = String(row.verified_by);
  } else {
    if (
      row.closed_at !== null
      || row.closed_by !== null
      || row.verified_at !== null
      || row.verified_by !== null
      || !isTimestamp(row.reopened_at)
      || rowSnapshot.reopen_count < 1
    ) {
      mismatch();
    }
    expectedEventKind = 'production_day_reopened';
    transitionActor = String(event.actor_user_id ?? '');
  }

  if (
    !isUuid(event.id)
    || event.tenant_id !== expected.tenant_id
    || event.business_id !== expected.business_id
    || event.kind !== expectedEventKind
    || !isUuid(event.actor_user_id)
    || event.actor_user_id !== transitionActor
    || typeof event.role !== 'string'
    || event.role.trim() === ''
    || event.device_id !== expected.device_id
    || event.client_uuid !== expected.request_id
    || !sameInstant(event.client_ts, expected.requested_at)
    || !isTimestamp(event.server_ts)
    || !Number.isSafeInteger(event.server_rev)
    || Number(event.server_rev) <= 0
    || !isHash(event.prev_hash)
    || !isHash(event.self_hash)
    || event.hash_version !== 2
    || !(
      event.anchor_id === null
      || isUuid(event.anchor_id)
    )
    || !isRecord(event.payload)
  ) {
    mismatch();
  }

  const payload = event.payload as Record<string, unknown>;
  if (
    payload.request_id !== expected.request_id
    || payload.production_day_id !== expected.production_day_id
    || payload.business_id !== expected.business_id
    || payload.sheet_date !== expected.sheet_date
  ) {
    mismatch();
  }
  if (expected.transition === 'close') {
    if (
      payload.closed_by_user_id !== transitionActor
      || !Number.isSafeInteger(payload.productions_count)
      || Number(payload.productions_count) < 0
      || payload.meals_total !== rowSnapshot.meals_total
      || payload.sample_meal_taken !== rowSnapshot.sample_meal_taken
    ) {
      mismatch();
    }
  } else if (expected.transition === 'verify') {
    if (
      payload.closed_by_user_id !== row.closed_by
      || payload.verified_by_user_id !== transitionActor
      || !sameInstant(payload.closed_at, row.closed_at)
      || payload.reopen_count !== rowSnapshot.reopen_count
    ) {
      mismatch();
    }
  } else if (
    payload.reopened_by_user_id !== transitionActor
    || payload.reopen_count !== rowSnapshot.reopen_count
  ) {
    mismatch();
  }

  return result as unknown as ProductionDayTransitionResultV1;
}
